import React, { createContext, useContext, useState, useEffect } from 'react';
import authService from '../services/auth';
import { useNavigate } from 'react-router-dom';
import { Profile } from '../types/profile';
import { User } from '../types/user';

export type { Profile };

interface AuthResponse {
  token: string;
  refreshToken: string;
  user: User;
  isNewUser?: boolean;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  profiles: Profile[];
  currentProfile: Profile | null;
  loading: boolean;
  setCurrentProfile: (profile: Profile | null) => void;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string) => Promise<{ isNewUser: boolean }>;
  logout: () => Promise<void>;
  googleLogin: (credential: string) => Promise<AuthResponse>;
  updateProfile: (data: Partial<Profile>) => Promise<void>;
  createProfile: (data: Partial<Profile>) => Promise<Profile>;
  deleteProfile: (profileId: string) => Promise<void>;
  switchProfile: (profileId: string) => Promise<Profile>;
  updateUser: (data: { name?: string; country?: string }) => Promise<any>;
  refreshProfile: () => Promise<Profile | null>;
  updatePassword: (currentPassword: string | undefined, newPassword: string) => Promise<void>;
  refreshSubscription: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, _setToken] = useState<string | null>(null);
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [currentProfile, setCurrentProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  const setToken = (newToken: string | null) => {
    if (newToken) {
      localStorage.setItem('token', newToken);
    } else {
      localStorage.removeItem('token');
    }
    authService.setToken(newToken);
    _setToken(newToken);
  };

  useEffect(() => {
    const initAuth = async () => {
      try {
        const storedToken = localStorage.getItem('token');
        const storedData = localStorage.getItem('saycard_user_data');
        const userData = storedData ? JSON.parse(storedData) : {};
        
        if (!storedToken) {
          setLoading(false);
          return;
        }

        // Ensure token is set in auth service before making request
        authService.setToken(storedToken);
        setToken(storedToken);
        
        // Set cached user data immediately if available
        const cachedUser = await authService.getCachedUser();
        if (cachedUser) {
          setUser({
            ...cachedUser,
            country: cachedUser.country || userData.country
          });
          
          if (Array.isArray(cachedUser.profiles)) {
            setProfiles(cachedUser.profiles);
            const lastUsedProfileId = localStorage.getItem('lastUsedProfileId');
            const storedProfile = localStorage.getItem('currentProfile');
            
            // Validate stored profile exists in current profiles
            const isStoredProfileValid = lastUsedProfileId && 
              cachedUser.profiles.some((p: Profile) => p._id === lastUsedProfileId);
            
            if (!isStoredProfileValid) {
              // Clear invalid stored profile
              localStorage.removeItem('lastUsedProfileId');
              localStorage.removeItem('currentProfile');
            }
            
            // First set the stored profile if available and valid
            if (storedProfile && isStoredProfileValid) {
              setCurrentProfile(JSON.parse(storedProfile));
            }
            
            // Determine which profile to use
            const profileToUse = isStoredProfileValid && lastUsedProfileId
              ? cachedUser.profiles.find((profile: Profile) => profile._id === lastUsedProfileId)
              : cachedUser.profiles.find((profile: Profile) => profile.isPrimary) || 
                (cachedUser.profiles.length > 0 ? cachedUser.profiles[0] : null);
              
            // Then fetch fresh data from server
            if (profileToUse?._id) {
              try {
                const response = await authService.getProfile(profileToUse._id);
                if (response.data && response.data.shortId) {
                  setCurrentProfile(response.data);
                  localStorage.setItem('currentProfile', JSON.stringify(response.data));
                  localStorage.setItem('lastUsedProfileId', response.data._id);
                }
              } catch (error: any) {
                // If profile not found, clear it and try primary/first profile
                if (error.response?.status === 404) {
                  localStorage.removeItem('lastUsedProfileId');
                  localStorage.removeItem('currentProfile');
                  
                  const fallbackProfile = cachedUser.profiles.find((p: Profile) => p.isPrimary) || 
                    (cachedUser.profiles.length > 0 ? cachedUser.profiles[0] : null);
                    
                  if (fallbackProfile?._id) {
                    try {
                      const fallbackResponse = await authService.getProfile(fallbackProfile._id);
                      if (fallbackResponse.data && fallbackResponse.data.shortId) {
                        setCurrentProfile(fallbackResponse.data);
                        localStorage.setItem('currentProfile', JSON.stringify(fallbackResponse.data));
                        localStorage.setItem('lastUsedProfileId', fallbackResponse.data._id);
                      }
                    } catch (fallbackError) {
                      console.error('Error loading fallback profile:', fallbackError);
                    }
                  }
                } else {
                  console.error('Error loading profile:', error);
                }
              }
            }
          }
        }
        
        try {
          const response = await authService.getSession();
          if (response?.user) {
            // Get stored user data
            const storedData = localStorage.getItem('saycard_user_data');
            const userData = storedData ? JSON.parse(storedData) : {};
            
            // Merge API response with stored data to preserve country
            const userWithStoredData = {
              ...response.user,
              country: response.user.country || userData.country
            };
            setUser(userWithStoredData);
            
            // Update localStorage with merged data
            localStorage.setItem('saycard_user_data', JSON.stringify({
              ...userData, // Preserve any other stored data
              name: userWithStoredData.name,
              email: userWithStoredData.email,
              country: userWithStoredData.country
            }));

            if (Array.isArray(response.user.profiles)) {
              setProfiles(response.user.profiles);
              const lastUsedProfileId = localStorage.getItem('lastUsedProfileId');
              const lastUsedProfile = lastUsedProfileId 
                ? response.user.profiles.find((profile: Profile) => profile._id === lastUsedProfileId)
                : null;
              const profileToUse = lastUsedProfile || 
                response.user.profiles.find((profile: Profile) => profile.isPrimary) || 
                (response.user.profiles.length > 0 ? response.user.profiles[0] : null);
              setCurrentProfile(profileToUse);
            }
          }
        } catch (error) {
          console.error('Session refresh error:', error);
          // On error, still use cached data
          if (!user && cachedUser) {
            setUser({
              ...cachedUser,
              country: cachedUser.country || userData.country
            });
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const response = await authService.login(email, password);
      setToken(response.token);
      setUser(response.user);
      
      // Set the first profile as current if available
      if (response.user.profiles?.length > 0) {
        setCurrentProfile(response.user.profiles[0]);
      }
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const register = async (email: string, password: string) => {
    try {
      const response = await authService.register(email, password);
      setToken(response.token);
      setUser(response.user);
      return { isNewUser: true };
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  };

  const logout = async () => {
    await authService.logout();
    setUser(null);
    setToken(null);
    setCurrentProfile(null);
    setProfiles([]);
    localStorage.removeItem('lastUsedProfileId');
    navigate('/login');
  };

  const googleLogin = async (credential: string) => {
    try {
      const response = await authService.googleAuth(credential);
      
      // Save tokens
      setToken(response.token);
      localStorage.setItem('refreshToken', response.refreshToken);
      
      if (response.user) {
        // Save user data including country
        localStorage.setItem('saycard_user_data', JSON.stringify({
          name: response.user.name,
          email: response.user.email,
          country: response.user.country
        }));
        
        setUser(response.user);
        
        // Set profiles if available
        if (Array.isArray(response.user.profiles)) {
          setProfiles(response.user.profiles);
          const primaryProfile = response.user.profiles.find((profile: Profile) => profile.isPrimary);
          setCurrentProfile(primaryProfile || response.user.profiles[0] || null);
        }
      }
      
      return response;
    } catch (error) {
      console.error('Google login error:', error);
      throw error;
    }
  };

  const createProfile = async (data: Partial<Profile>) => {
    if (!user) throw new Error('No user logged in');
    
    try {
      const createdProfile = await authService.createProfile(data);
      
      // Update user state with the new profile
      setUser(prevUser => {
        if (!prevUser) return null;
        const profiles = prevUser.profiles || [];
        const updatedUser = {
          ...prevUser,
          profiles: [...profiles, createdProfile]
        };
        
        // Update localStorage with user data including country
        const storedData = localStorage.getItem('saycard_user_data');
        const userData = storedData ? JSON.parse(storedData) : {};
        const userDataToStore = {
          ...userData,
          name: updatedUser.name,
          email: updatedUser.email,
          country: updatedUser.country
        };
        
        localStorage.setItem('saycard_user_data', JSON.stringify(userDataToStore));
        
        return updatedUser;
      });
      
      // Set as current profile since it's the first one
      setCurrentProfile(createdProfile);
      setProfiles(prev => [...prev, createdProfile]);
      
      return createdProfile;
    } catch (error) {
      console.error('Create profile error:', error);
      throw error;
    }
  };

  const updateProfile = async (data: Partial<Profile>) => {
    if (!user) throw new Error('No user logged in');

    try {
      const updatedProfile = await authService.updateProfile(data);
      
      // Update profiles state
      setProfiles(prev => 
        prev.map(profile => profile._id === updatedProfile._id ? updatedProfile : profile)
      );
      
      // Update current profile if it's the one being updated
      if (currentProfile?._id === updatedProfile._id) {
        setCurrentProfile(updatedProfile);
      }
      
      // Update user state
      setUser(prevUser => {
        if (!prevUser) return null;
        return {
          ...prevUser,
          profiles: prevUser.profiles.map(profile => 
            profile._id === updatedProfile._id ? updatedProfile : profile
          )
        };
      });
    } catch (error) {
      console.error('Update profile error:', error);
      throw error;
    }
  };

  const deleteProfile = async (profileId: string) => {
    if (!user) throw new Error('No user logged in');

    try {
      await authService.deleteProfile(profileId);
      
      // Update states
      setProfiles(prev => prev.filter(profile => profile._id !== profileId));
      
      if (currentProfile?._id === profileId) {
        const remainingProfiles = profiles.filter(profile => profile._id !== profileId);
        setCurrentProfile(remainingProfiles[0] || null);
      }
      
      setUser(prevUser => {
        if (!prevUser) return null;
        return {
          ...prevUser,
          profiles: prevUser.profiles.filter(profile => profile._id !== profileId)
        };
      });
    } catch (error) {
      console.error('Delete profile error:', error);
      throw error;
    }
  };

  const switchProfile = async (profileId: string) => {
    try {
      // Get fresh profile data from server
      const response = await authService.getProfile(profileId);
      let profile = response.data;
      
      if (!profile) {
        throw new Error('Profile not found');
      }

      if (!profile.shortId) {
        console.error('Profile missing shortId, attempting to refresh');
        // Try to refresh the profile one more time
        const refreshResponse = await authService.getProfile(profileId);
        if (!refreshResponse.data?.shortId) {
          throw new Error('Profile shortId not available');
        }
        profile = refreshResponse.data;
      }

      // Update all relevant state
      setCurrentProfile(profile);
      localStorage.setItem('lastUsedProfileId', profileId);
      
      // Update profiles array with fresh data
      setProfiles(prev => 
        prev.map(p => p._id === profile._id ? profile : p)
      );

      return profile;
    } catch (error) {
      console.error('Error switching profile:', error);
      throw error; // Re-throw to allow handling by caller
    }
  };

  const updateUser = async (data: { name?: string; country?: string }) => {
    // Update localStorage first
    const storedData = localStorage.getItem('saycard_user_data');
    const userData = storedData ? JSON.parse(storedData) : {};
    const userDataToStore = {
      ...userData,
      ...data
    };
    
    localStorage.setItem('saycard_user_data', JSON.stringify(userDataToStore));
    
    // Update local state immediately
    setUser(prev => prev ? { ...prev, ...data } : null);

    // Then update server
    try {
      const response = await authService.updateUser(data);
      
      // If server update fails, revert localStorage and state
      if (!response) {
        localStorage.setItem('saycard_user_data', JSON.stringify(userData));
        setUser(prev => prev ? { ...prev, country: userData.country } : null);
        throw new Error('Failed to update user');
      }
      
      return response;
    } catch (error) {
      console.error('Failed to update user:', error);
      throw error;
    }
  };

  const refreshProfile = async (): Promise<Profile | null> => {
    if (!user?._id || !currentProfile?._id) return null;

    try {
      const response = await authService.getProfile(currentProfile._id);
      const profile = response.data;
      setCurrentProfile(profile);
      return profile;
    } catch (error) {
      console.error('Error refreshing profile:', error);
      return null;
    }
  };

  const updatePassword = async (currentPassword: string | undefined, newPassword: string) => {
    try {
      await authService.updatePassword(currentPassword, newPassword);
    } catch (error: any) {
      throw new Error(error.message || 'Failed to update password');
    }
  };

  const refreshSubscription = async () => {
    try {
      const response = await authService.getSession();
      if (response?.user) {
        setUser(response.user);
      }
    } catch (error) {
      console.error('Error refreshing subscription:', error);
    }
  };

  // Check URL parameters for subscription success
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const success = params.get('success');
    const tier = params.get('tier');
    
    if (success === 'true' && tier) {
      refreshSubscription();
      // Clear URL parameters
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const value = {
    user,
    token,
    profiles,
    currentProfile,
    loading,
    setCurrentProfile,
    login,
    register,
    logout,
    googleLogin,
    updateProfile,
    createProfile,
    deleteProfile,
    switchProfile,
    updateUser,
    refreshProfile,
    updatePassword,
    refreshSubscription,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;